// src/pages/employee/EmployeeManagement.js
// Gestión de empleados / groomers (employee.php)

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Table, Modal } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { getEmployees, getEmployeeById, createEmployee, updateEmployee, deleteEmployee } from '../../services/employeeApi';
import { getEmployeeRoles } from '../../services/employeeRoleApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [employeeRoles, setEmployeeRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    document: '',
    id_document_type: '',
    id_person_type: '',
    surname: '',
    lastname: '',
    second_surname: '',
    salary: '',
    initial_date: '',
    end_date: '',
    id_status: '',
    id_employee_role: '',
    agenda_color: '#3B82F6'
  });

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const { ok, status, data } = await getEmployees();
      if (ok && (status === 200 || data?.code === 200)) {
        const list = data.data || data.message || [];
        setEmployees(Array.isArray(list) ? list : []);
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error cargando empleados', 'error');
      }
    } catch (error) {
      console.error('Error cargando empleados:', error);
      showAlert('Error inesperado al cargar empleados', 'error');
    } finally {
      setLoading(false);
    }
  }, [showAlert, getErrorMessage]);

  const loadRoles = useCallback(async () => {
    const { ok, data } = await getEmployeeRoles();
    if (ok && (data?.code === 200 || data?.data)) {
      const list = data.data || data.message || [];
      setEmployeeRoles(Array.isArray(list) ? list : []);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
    loadRoles();
  }, [loadEmployees, loadRoles]);

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      document: '',
      id_document_type: '1',
      id_person_type: '1',
      surname: '',
      lastname: '',
      second_surname: '',
      salary: '',
      initial_date: '',
      end_date: '',
      id_status: '1',
      id_employee_role: '',
      agenda_color: '#3B82F6'
    });
    setSelectedEmployee(null);
    setShowCreateModal(true);
  };

  const handleOpenEdit = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      name: emp.name || '',
      document: emp.document || '',
      id_document_type: emp.id_document_type ?? '',
      id_person_type: emp.id_person_type ?? '',
      surname: emp.surname || '',
      lastname: emp.lastname || '',
      second_surname: emp.second_surname || '',
      salary: emp.salary ?? '',
      initial_date: emp.initial_date ? emp.initial_date.slice(0, 10) : '',
      end_date: emp.end_date ? emp.end_date.slice(0, 10) : '',
      id_status: emp.id_status ?? '',
      id_employee_role: emp.id_employee_role ?? '',
      agenda_color: emp.agenda_color || '#3B82F6'
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (emp) => {
    setSelectedEmployee(emp);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedEmployee(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.document) {
      showAlert('Nombre y documento son obligatorios', 'error');
      return;
    }
    try {
      setModalLoading(true);
      const payload = {
        person: {
          name: formData.name,
          document: formData.document,
          id_document_type: parseInt(formData.id_document_type, 10) || 1,
          id_person_type: parseInt(formData.id_person_type, 10) || 1,
          surname: formData.surname || undefined,
          lastname: formData.lastname || undefined,
          second_surname: formData.second_surname || undefined
        },
        salary: formData.salary ? parseFloat(formData.salary) : undefined,
        initial_date: formData.initial_date || undefined,
        end_date: formData.end_date || undefined,
        id_status: formData.id_status ? parseInt(formData.id_status, 10) : undefined,
        id_role: formData.id_employee_role ? parseInt(formData.id_employee_role, 10) : undefined,
        id_employee_role: formData.id_employee_role ? parseInt(formData.id_employee_role, 10) : undefined,
        agenda_color: formData.agenda_color || undefined
      };
      const { ok, status, data } = await createEmployee(payload);
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Empleado creado', 'success');
        handleCloseModals();
        loadEmployees();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al crear empleado', 'error');
      }
    } catch (error) {
      console.error('Error creando empleado:', error);
      showAlert('Error inesperado al crear empleado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedEmployee?.id_employee) return;
    try {
      setModalLoading(true);
      const payload = {
        salary: formData.salary ? parseFloat(formData.salary) : undefined,
        initial_date: formData.initial_date || undefined,
        end_date: formData.end_date || undefined,
        id_status: formData.id_status ? parseInt(formData.id_status, 10) : undefined,
        id_role: formData.id_employee_role ? parseInt(formData.id_employee_role, 10) : undefined,
        agenda_color: formData.agenda_color || undefined
      };
      const { ok, status, data } = await updateEmployee(selectedEmployee.id_employee, payload);
      if (ok && status === 200) {
        showAlert(data?.message || 'Empleado actualizado', 'success');
        handleCloseModals();
        loadEmployees();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al actualizar empleado', 'error');
      }
    } catch (error) {
      console.error('Error actualizando empleado:', error);
      showAlert('Error inesperado al actualizar empleado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedEmployee?.id_employee) return;
    try {
      setModalLoading(true);
      const { ok, status, data } = await deleteEmployee(selectedEmployee.id_employee);
      if (ok && status === 200) {
        showAlert(data?.message || 'Empleado eliminado', 'success');
        handleCloseModals();
        loadEmployees();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al eliminar empleado', 'error');
      }
    } catch (error) {
      console.error('Error eliminando empleado:', error);
      showAlert('Error inesperado al eliminar empleado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-user-tie me-2"></i>
                  Gestión de empleados
                </h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" onClick={() => window.location.href = '/gestion-usuarios'}>
                    <i className="fas fa-users me-1"></i>
                    Usuarios
                  </Button>
                  <Button variant="primary" onClick={handleOpenCreate}>
                    <i className="fas fa-plus me-1"></i>
                    Nuevo empleado
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre completo</th>
                        <th>Documento</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Color agenda</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">
                            No hay empleados registrados
                          </td>
                        </tr>
                      ) : (
                        employees.map((emp) => (
                          <tr key={emp.id_employee}>
                            <td>{emp.id_employee}</td>
                            <td>{emp.full_name || [emp.name, emp.surname, emp.lastname].filter(Boolean).join(' ') || '-'}</td>
                            <td>{emp.document || '-'}</td>
                            <td>{emp.employee_role_name || '-'}</td>
                            <td>{emp.status_name || '-'}</td>
                            <td>
                              {emp.agenda_color ? (
                                <span
                                  className="d-inline-block rounded px-2 py-1 text-white small"
                                  style={{ backgroundColor: emp.agenda_color }}
                                >
                                  {emp.agenda_color}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="text-center">
                              <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleOpenEdit(emp)}>
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button variant="outline-danger" size="sm" onClick={() => handleOpenDelete(emp)}>
                                <i className="fas fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal show={showCreateModal} onHide={handleCloseModals} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Nuevo empleado</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCreate}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre *</Form.Label>
                    <Form.Control name="name" value={formData.name} onChange={handleChange} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Apellido</Form.Label>
                    <Form.Control name="surname" value={formData.surname} onChange={handleChange} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Documento *</Form.Label>
                    <Form.Control name="document" value={formData.document} onChange={handleChange} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rol empleado</Form.Label>
                    <Form.Select name="id_employee_role" value={formData.id_employee_role} onChange={handleChange}>
                      <option value="">Seleccionar</option>
                      {employeeRoles.map((r) => (
                        <option key={r.id_employee_role} value={r.id_employee_role}>
                          {r.role_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Color agenda (groomer)</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        name="agenda_color"
                        value={formData.agenda_color}
                        onChange={handleChange}
                        style={{ width: 48, height: 38 }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.agenda_color}
                        onChange={(e) => setFormData((p) => ({ ...p, agenda_color: e.target.value }))}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Salario</Form.Label>
                    <Form.Control type="number" step="0.01" name="salary" value={formData.salary} onChange={handleChange} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha inicio</Form.Label>
                    <Form.Control type="date" name="initial_date" value={formData.initial_date} onChange={handleChange} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha fin</Form.Label>
                    <Form.Control type="date" name="end_date" value={formData.end_date} onChange={handleChange} />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : 'Crear'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showEditModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Editar empleado</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Rol empleado</Form.Label>
                <Form.Select name="id_employee_role" value={formData.id_employee_role} onChange={handleChange}>
                  <option value="">Seleccionar</option>
                  {employeeRoles.map((r) => (
                    <option key={r.id_employee_role} value={r.id_employee_role}>
                      {r.role_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Color agenda</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="color"
                    name="agenda_color"
                    value={formData.agenda_color}
                    onChange={handleChange}
                    style={{ width: 48, height: 38 }}
                  />
                  <Form.Control
                    type="text"
                    value={formData.agenda_color}
                    onChange={(e) => setFormData((p) => ({ ...p, agenda_color: e.target.value }))}
                  />
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Salario</Form.Label>
                <Form.Control type="number" step="0.01" name="salary" value={formData.salary} onChange={handleChange} />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha inicio</Form.Label>
                    <Form.Control type="date" name="initial_date" value={formData.initial_date} onChange={handleChange} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha fin</Form.Label>
                    <Form.Control type="date" name="end_date" value={formData.end_date} onChange={handleChange} />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showDeleteModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar empleado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Eliminar al empleado <strong>{selectedEmployee?.full_name || selectedEmployee?.name}</strong>? Esta acción no se puede deshacer.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
            </Button>
          </Modal.Footer>
        </Modal>

        <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
      </Container>
    </>
  );
};

export default EmployeeManagement;
